import axios from "axios";
import Config from "../utils/Config";
import { INDUSTRIES, BETTING_GAMING_ITEMS, LEGAL_ITEMS } from "../data/categoryData";

const BASE_URL = Config.API_BASE_URL;

/** How long reused category data stays valid before the next soft fetch may hit the network. */
const CATEGORIES_CACHE_TTL_MS = 5 * 60 * 1000;

const DEFAULT_INDUSTRY_COLORS = [
  "#1e40af",
  "#dc2626",
  "#E5B800",
  "#7c3aed",
  "#f97316",
  "#16a34a",
];

const slugify = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

const normalizeKey = (value = "") =>
  String(value).toLowerCase().replace(/[^a-z0-9]+/g, "");

/**
 * Match a CMS/API subcategory row to a static catalog item (same topic, different wording).
 * Used so pinned legal & betting menus keep stable slugs but can show backend `description`.
 */
function bestApiSubcategoryForStaticItem(subCategories, staticItem) {
  const subs = Array.isArray(subCategories) ? subCategories : [];
  if (!subs.length || !staticItem?.name) return null;
  const words =
    String(staticItem.name)
      .toLowerCase()
      .match(/[a-z0-9]{3,}/g) || [];
  const staticKey = normalizeKey(staticItem.name);
  let best = null;
  let bestScore = -1;
  for (const sub of subs) {
    const nk = normalizeKey(sub?.name || "");
    if (!nk) continue;
    let wordHits = 0;
    for (const w of words) {
      if (nk.includes(w)) wordHits += 1;
    }
    const prefixLen = Math.min(12, staticKey.length, nk.length);
    const prefixBoost =
      prefixLen >= 6 && staticKey.slice(0, prefixLen) === nk.slice(0, prefixLen) ? 4 : 0;
    const score = wordHits + prefixBoost;
    if (score > bestScore) {
      bestScore = score;
      best = sub;
    }
  }
  if (bestScore < 2) return null;
  return best;
}

function pinnedDescriptionFromApi(subCategories, staticItem) {
  const apiSub = bestApiSubcategoryForStaticItem(subCategories, staticItem);
  const fromApi = (apiSub?.description || "").trim();
  if (fromApi) return fromApi;
  return (staticItem.description || "").trim();
}

/** Title case for display (API sends SCREAMING CAPS labels). */
const toTitleCase = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const BACKEND_CATEGORY_TO_INDUSTRY_ID = {
  realestatemortgages: "real-estate",
  legal: "legal",
  nutritionmealplansrecipes: "nutrition",
  health: "health",
  jobscareer: "careers",
  bettinggaming: "betting-gaming",
};

const emptyNormalized = () => ({
  industries: [],
  menuByIndustry: {},
  categoryOptions: [],
});

class UtilsServices {
  constructor() {
    this._categoriesCache = null;
    this._categoriesInFlightSoft = null;
  }

  getHeaders() {
    return {
      headers: {
        "Content-Type": "application/json",
      },
    };
  }

  /** Pin Legal first everywhere the API list is rendered. */
  sortCategoriesLegalFirst(rawCategories) {
    const list = Array.isArray(rawCategories) ? [...rawCategories] : [];
    return list.sort((a, b) => {
      const aKey = normalizeKey(a?.name || "");
      const bKey = normalizeKey(b?.name || "");
      const aLegal =
        aKey === "legal" || BACKEND_CATEGORY_TO_INDUSTRY_ID[aKey] === "legal";
      const bLegal =
        bKey === "legal" || BACKEND_CATEGORY_TO_INDUSTRY_ID[bKey] === "legal";
      if (aLegal && !bLegal) return -1;
      if (!aLegal && bLegal) return 1;
      return 0;
    });
  }

  mapSignupCategoriesDisplay(sortedRaw) {
    return sortedRaw.map((cat) => ({
      ...cat,
      name: toTitleCase(cat?.name || ""),
      sub_categories: (Array.isArray(cat?.sub_categories)
        ? cat.sub_categories
        : []
      ).map((sub) => ({
        ...sub,
        name: toTitleCase(sub?.name || ""),
      })),
    }));
  }

  async loadCategoriesBundleFromNetwork() {
    try {
      const response = await axios.get(`${BASE_URL}/categories`, this.getHeaders());
      const envelope = response?.data?.data;
      const statusOk = envelope?.status_code === 200;
      const rawList = Array.isArray(envelope?.data) ? envelope.data : [];

      if (!statusOk) {
        return {
          error: true,
          message: envelope?.reason || "Unexpected categories response",
        };
      }

      const sorted = this.sortCategoriesLegalFirst(rawList);
      const normalized = this.normalizeCategoryPayload(sorted);
      const signupCategories = this.mapSignupCategoriesDisplay(sorted);

      return {
        normalized,
        signupCategories,
        fetchEnvelope: envelope,
        pointInTime: envelope?.point_in_time ?? null,
      };
    } catch (error) {
      return {
        error: true,
        message:
          error?.response?.data?.reason ||
          error?.response?.data?.message ||
          error?.message ||
          "Sorry, An Error Occurred during category Fetch",
      };
    }
  }

  /**
   * Single-flight cache for ordinary navigations; `force: true` skips TTL and
   * always requests fresh data (e.g. after admin edits).
   */
  async ensureCategoriesBundle(force = false) {
    const now = Date.now();
    if (
      !force &&
      this._categoriesCache &&
      now - this._categoriesCache.cachedAt < CATEGORIES_CACHE_TTL_MS
    ) {
      return this._categoriesCache.bundle;
    }

    if (!force && this._categoriesInFlightSoft) {
      return this._categoriesInFlightSoft;
    }

    const work = this.loadCategoriesBundleFromNetwork()
      .then((bundle) => {
        if (bundle && !bundle.error) {
          this._categoriesCache = { cachedAt: Date.now(), bundle };
        }
        return bundle;
      })
      .finally(() => {
        if (!force) {
          this._categoriesInFlightSoft = null;
        }
      });

    if (!force) {
      this._categoriesInFlightSoft = work;
    }

    return work;
  }

  normalizeCategoryPayload(rawCategories = []) {
    const categories = this.sortCategoriesLegalFirst(
      Array.isArray(rawCategories) ? rawCategories : [],
    );
    const legacyIndustryById = Object.fromEntries(
      INDUSTRIES.map((item) => [item.id, item]),
    );
    const industries = categories.map((category, index) => {
      const rawLabel = category?.name || `Category ${index + 1}`;
      const normalizedCategoryKey = normalizeKey(rawLabel);
      const mappedIndustryId =
        BACKEND_CATEGORY_TO_INDUSTRY_ID[normalizedCategoryKey];
      const legacyIndustry = mappedIndustryId
        ? legacyIndustryById[mappedIndustryId]
        : null;
      const displayLabel =
        legacyIndustry?.label || toTitleCase(rawLabel);
      const resolvedIndustryId =
        legacyIndustry?.id ||
        category?.slug ||
        slugify(rawLabel) ||
        `category-${index + 1}`;
      return {
        id: resolvedIndustryId,
        path: `/${resolvedIndustryId}`,
        label: displayLabel,
        color:
          legacyIndustry?.color ||
          DEFAULT_INDUSTRY_COLORS[index % DEFAULT_INDUSTRY_COLORS.length],
        description: legacyIndustry?.description || category?.description || "",
        comingSoonSub:
          legacyIndustry?.comingSoonSub || category?.subtitle || "",
      };
    });

    const menuByIndustry = {};
    categories.forEach((category, index) => {
      const rawLabel = category?.name || `Category ${index + 1}`;
      const normalizedCategoryKey = normalizeKey(rawLabel);
      const mappedIndustryId =
        BACKEND_CATEGORY_TO_INDUSTRY_ID[normalizedCategoryKey];
      const industryId =
        mappedIndustryId ||
        category?.slug ||
        slugify(rawLabel) ||
        `category-${index + 1}`;
      const subCategories = Array.isArray(category?.sub_categories)
        ? category.sub_categories
        : [];

      /** Rich legal hubs + registry slugs must stay aligned with `LEGAL_ITEMS` — never derive from API alone. */
      if (industryId === "legal") {
        menuByIndustry[industryId] = LEGAL_ITEMS.map((item) => ({
          id: `leg-${item.id}`,
          slug: item.slug,
          label: item.name,
          path: `/legal/${item.slug}`,
          description: pinnedDescriptionFromApi(subCategories, item),
          icon: item.icon,
          color: item.color,
        }));
        return;
      }

      /** Rich topic pages + registry slugs must stay aligned with `BETTING_GAMING_ITEMS` — never derive from API alone. */
      if (industryId === "betting-gaming") {
        menuByIndustry[industryId] = BETTING_GAMING_ITEMS.map((item) => ({
          id: `bet-${item.id}`,
          slug: item.slug,
          label: item.name,
          path: `/betting-gaming/${item.slug}`,
          description: pinnedDescriptionFromApi(subCategories, item),
          icon: item.icon,
          color: item.color,
        }));
        return;
      }

      menuByIndustry[industryId] = subCategories.map((subCategory, subIndex) => {
        const rawSubLabel = subCategory?.name || `Subcategory ${subIndex + 1}`;
        const subLabel = toTitleCase(rawSubLabel);
        const subSlug =
          subCategory?.slug ||
          slugify(rawSubLabel) ||
          `${industryId}-${subIndex + 1}`;
        return {
          id: `${industryId}-${subCategory?._id || subCategory?.id || subSlug}`,
          slug: subSlug,
          label: subLabel,
          path: `/${industryId}/${subSlug}`,
          description: subCategory?.description || "",
        };
      });
    });

    return {
      industries,
      menuByIndustry,
      categoryOptions: industries.map((industry) => industry.label),
    };
  }

  async fetchCategories(options = {}) {
    const res = await this.fetchCategoriesNormalized(options);
    if (!res.status) {
      return {
        status: false,
        data: null,
        message: res.message,
      };
    }
    const envelope = res.data;
    return {
      status: true,
      data: {
        ...envelope,
        data: res.signupCategories,
      },
      message: res.message,
    };
  }

  async fetchCategoriesNormalized(options = {}) {
    const force = Boolean(options?.force);
    const bundle = await this.ensureCategoriesBundle(force);
    if (!bundle || bundle.error) {
      return {
        status: false,
        data: null,
        message:
          bundle?.message ||
          "Sorry, An Error Occured during category Fetch",
        normalized: emptyNormalized(),
        signupCategories: [],
        pointInTime: null,
      };
    }
    const envelope = bundle.fetchEnvelope;
    return {
      status: true,
      data: envelope,
      message: envelope?.reason || "Successfully Fetched Categories",
      normalized: bundle.normalized,
      signupCategories: bundle.signupCategories,
      pointInTime: bundle.pointInTime,
    };
  }
}

const utilsService = new UtilsServices();
export default utilsService;
